export function fmtKickoff(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleString(undefined, { hour: '2-digit', minute: '2-digit' })
}

/**
 * Format elapsed match time with optional added/stoppage time
 * @param elapsed - Current match minute (e.g., 45, 90)
 * @param elapsedExtra - Added/stoppage time minutes (e.g., 3 for +3)
 * @returns Formatted string like "45'" or "45+3'"
 */
export function fmtElapsed(elapsed: number, elapsedExtra?: number | null): string {
  if (elapsedExtra && elapsedExtra > 0) {
    return `${elapsed}+${elapsedExtra}'`
  }
  return `${elapsed}'`
}

/**
 * Format a date for display as a section header
 */
export function fmtDateHeader(iso: string): string {
  const d = new Date(iso)
  const now = new Date()
  const tomorrow = new Date(now)
  tomorrow.setDate(tomorrow.getDate() + 1)

  if (d.toDateString() === now.toDateString()) {
    return 'Today'
  }
  if (d.toDateString() === tomorrow.toDateString()) {
    return 'Tomorrow'
  }

  return d.toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'short'
  })
}

/**
 * Get the date key (YYYY-MM-DD) for grouping fixtures
 */
export function getDateKey(iso: string): string {
  const d = new Date(iso)
  return d.toISOString().split('T')[0]
}

// ============================================================
// Odds Utilities
// ============================================================

/**
 * Map angle market to odds market slug
 *
 * Angle market IDs have formats like:
 * - corners_over_18 (team ID)
 * - total_corners_191 (fixture ID)
 * - goals_over_19
 * - btts_191
 * - player_goals_577 (player ID)
 * - sot_over_85 (shots on target, team ID)
 * - cards_over_86 (cards, team ID)
 *
 * @param angleMarket - The angle market ID
 * @param teamType - 'home' | 'away' | 'total' for team-specific markets
 */
export function angleMarketToOddsMarket(
  angleMarket: string,
  teamType?: 'home' | 'away' | 'total'
): string | null {
  const lowerMarket = angleMarket.toLowerCase()

  // New market_type based mappings (from restructured backend)
  if (lowerMarket === 'match-winner') return 'match-winner'
  if (lowerMarket === 'double-chance') return 'double-chance'
  if (lowerMarket === 'draw-no-bet') return 'draw-no-bet'
  if (lowerMarket === 'btts') return 'btts'
  if (lowerMarket === 'match-goals-over-under') return 'goals-over-under'
  if (lowerMarket === 'team-goals-over-under') {
    if (teamType === 'home') return 'home-goals-over-under'
    if (teamType === 'away') return 'away-goals-over-under'
    return 'goals-over-under'
  }
  if (lowerMarket === 'match-corners-over-under') return 'corners-over-under'
  if (lowerMarket === 'team-corners-over-under') {
    if (teamType === 'home') return 'home-corners-over-under'
    if (teamType === 'away') return 'away-corners-over-under'
    return 'corners-over-under'
  }
  if (lowerMarket === 'match-cards-over-under') return 'cards-over-under'
  if (lowerMarket === 'team-cards-over-under') {
    if (teamType === 'home') return 'home-yellow-cards-over-under'
    if (teamType === 'away') return 'away-yellow-cards-over-under'
    return 'cards-over-under'
  }
  // Yellow card specific markets - fall back to total cards since team-specific yellow card odds don't exist
  if (lowerMarket === 'yellow-cards-over-under') return 'cards-over-under'
  if (lowerMarket === 'home-yellow-cards-over-under') return 'cards-over-under'
  if (lowerMarket === 'away-yellow-cards-over-under') return 'cards-over-under'

  // Shots on target (team and player)
  if (lowerMarket === 'team-sot-over-under' || lowerMarket === 'player-sot-over-under') {
    if (teamType === 'home') return 'home-shots-on-target-over-under'
    if (teamType === 'away') return 'away-shots-on-target-over-under'
    return 'shots-on-target-over-under'
  }
  if (lowerMarket === 'match-sot-over-under') return 'shots-on-target-over-under'

  // Total shots (not just on target)
  if (lowerMarket === 'match-shots-over-under') return 'shots-over-under'
  if (lowerMarket === 'team-shots-over-under') {
    if (teamType === 'home') return 'home-shots-over-under'
    if (teamType === 'away') return 'away-shots-over-under'
    return 'shots-over-under'
  }
  if (lowerMarket === 'player-shots-over-under') return 'player-shots-over-under'

  // Fouls markets
  if (lowerMarket === 'match-fouls-over-under') return 'fouls-over-under'
  if (lowerMarket === 'team-fouls-over-under') {
    if (teamType === 'home') return 'home-fouls-over-under'
    if (teamType === 'away') return 'away-fouls-over-under'
    return 'fouls-over-under'
  }
  // Player props
  if (lowerMarket === 'anytime-goalscorer') return 'anytime-goalscorer'
  if (lowerMarket === 'player-booked') return 'player-booked'
  if (lowerMarket === 'anytime-assist') return 'player-assists'
  if (lowerMarket === 'score-or-assist') return 'player-score-or-assist'
  if (lowerMarket === 'player-multi-goals') return 'player-multi-goals'
  if (lowerMarket === 'player-sot-over-under') return 'player-shots-on-target'
  if (lowerMarket === 'player-shots-over-under') return 'player-shots'
  if (lowerMarket === 'player-fouls-over-under') return 'player-fouls'
  if (lowerMarket === 'goalkeeper-saves-over-under') return 'goalkeeper-saves'

  // Clean sheet markets
  if (lowerMarket === 'clean-sheet') {
    if (teamType === 'home') return 'home-clean-sheet'
    if (teamType === 'away') return 'away-clean-sheet'
    return 'clean-sheet'
  }

  // Win to nil markets
  if (lowerMarket === 'win-to-nil') {
    if (teamType === 'home') return 'home-win-to-nil'
    if (teamType === 'away') return 'away-win-to-nil'
    return 'win-to-nil'
  }

  // Team to score first
  if (lowerMarket === 'team-to-score-first') return 'team-to-score-first'

  // First half markets
  if (lowerMarket === 'first-half-goals-over-under') return 'first-half-goals-over-under'
  if (lowerMarket === 'first-half-btts') return 'first-half-btts'

  // Second half markets
  if (lowerMarket === 'second-half-goals-over-under') return 'second-half-goals-over-under'
  if (lowerMarket === 'second-half-btts') return 'second-half-btts'

  // HT/FT result
  if (lowerMarket === 'htft-result') return 'htft-result'

  // Combo markets
  if (lowerMarket === 'result-btts') return 'result-btts'
  if (lowerMarket === 'match-odds-ou-goals') return 'result-total-goals'
  if (lowerMarket === 'btts-ou-25' || lowerMarket === 'btts-total-goals') return 'btts-total-goals'

  // Legacy mappings (for backward compatibility)
  // Total/match-level markets
  if (lowerMarket.startsWith('total_corners')) {
    return 'corners-over-under'
  }
  if (lowerMarket.startsWith('total_goals')) {
    return 'goals-over-under'
  }
  if (lowerMarket.startsWith('total_cards')) {
    return 'cards-over-under'
  }

  // Team-specific corners
  if (lowerMarket.startsWith('corners_over')) {
    if (teamType === 'home') return 'home-corners-over-under'
    if (teamType === 'away') return 'away-corners-over-under'
    return 'corners-over-under' // fallback to total
  }

  // Team-specific goals
  if (lowerMarket.startsWith('goals_over')) {
    if (teamType === 'home') return 'home-goals-over-under'
    if (teamType === 'away') return 'away-goals-over-under'
    return 'goals-over-under' // fallback to total
  }

  // Team-specific cards
  if (lowerMarket.startsWith('cards_over')) {
    // Map to yellow cards for team-specific, total cards for match
    if (teamType === 'home') return 'home-yellow-cards-over-under'
    if (teamType === 'away') return 'away-yellow-cards-over-under'
    return 'cards-over-under'
  }

  // Shots on target
  if (lowerMarket.startsWith('sot_over') || lowerMarket.includes('shots_on_target')) {
    if (teamType === 'home') return 'home-shots-on-target-over-under'
    if (teamType === 'away') return 'away-shots-on-target-over-under'
    return 'shots-on-target-over-under'
  }

  // BTTS
  if (lowerMarket.startsWith('btts')) {
    return 'btts'
  }

  // Player markets
  if (lowerMarket.startsWith('player_goals') || lowerMarket.includes('anytime_scorer')) {
    return 'anytime-goalscorer'
  }
  if (lowerMarket.startsWith('player_card') || lowerMarket.includes('to_be_booked')) {
    return 'player-booked'
  }

  // Match winner (legacy)
  if (lowerMarket === 'match_winner' || lowerMarket.startsWith('match_result')) {
    return 'match-winner'
  }

  // Double chance (legacy)
  if (lowerMarket.startsWith('double_chance')) {
    return 'double-chance'
  }

  // Draw no bet (legacy)
  if (lowerMarket.startsWith('dnb_')) {
    return 'draw-no-bet'
  }

  // Simplified category names (used by SavedBet.marketType)
  // These are fallback mappings for when the detailed market type isn't available
  if (lowerMarket === 'goals') {
    if (teamType === 'home') return 'home-goals-over-under'
    if (teamType === 'away') return 'away-goals-over-under'
    return 'goals-over-under'
  }
  if (lowerMarket === 'corners') {
    if (teamType === 'home') return 'home-corners-over-under'
    if (teamType === 'away') return 'away-corners-over-under'
    return 'corners-over-under'
  }
  if (lowerMarket === 'cards') {
    if (teamType === 'home') return 'home-yellow-cards-over-under'
    if (teamType === 'away') return 'away-yellow-cards-over-under'
    return 'cards-over-under'
  }
  if (lowerMarket === 'shots') {
    if (teamType === 'home') return 'home-shots-over-under'
    if (teamType === 'away') return 'away-shots-over-under'
    return 'shots-over-under'
  }
  if (lowerMarket === 'shots_on_target') {
    if (teamType === 'home') return 'home-shots-on-target-over-under'
    if (teamType === 'away') return 'away-shots-on-target-over-under'
    return 'shots-on-target-over-under'
  }

  // No mapping available for fouls, assists, general shots (not typical betting markets)
  return null
}

/**
 * Calculate combined odds for an accumulator
 */
export function calculateCombinedOdds(odds: number[]): number {
  return odds.reduce((acc, o) => acc * o, 1)
}

/**
 * Format odds updated timestamp as relative time
 */
export function formatOddsUpdatedAt(isoString: string | null): string {
  if (!isoString) return 'Unknown'

  const updated = new Date(isoString)
  const now = new Date()
  const diffMs = now.getTime() - updated.getTime()
  const diffMins = Math.floor(diffMs / (1000 * 60))

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`

  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours}h ago`

  return updated.toLocaleDateString()
}
